import { CommonModule, isPlatformBrowser } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, DestroyRef, Inject, InjectionToken, OnInit, PLATFORM_ID, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef } from "@angular/material/dialog";
import { CBox_PublicRolePermissionMapping, RoleExtensionsEnum } from "@server/services/cbox/public/api/v1/enforcers/entity_permission/types";
import { CBox_PublicSuccessResponse } from "@server/services/cbox/public/api/v1/resources/common/request_base/types";
import { CBox_GetLockerShortData } from "@server/services/cbox/public/api/v1/resources/locker/types";
import { CBox_ActionCreateUserResponse, CBox_ActionUpdateOtherUserData, CBox_GetOtherUserResponse, CBox_GetSelfUserResponse } from "@server/services/cbox/public/api/v1/resources/user/types";
import { FormattedPermission } from "app/profile/agents/types";
import { Message } from "primeng/api";
import { AutoFocusModule } from "primeng/autofocus";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { DividerModule } from "primeng/divider";
import { DropdownModule } from "primeng/dropdown";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { InputTextModule } from "primeng/inputtext";
import { InputTextareaModule } from "primeng/inputtextarea";
import { MessagesModule } from "primeng/messages";
import { MultiSelectModule } from "primeng/multiselect";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { TableModule } from "primeng/table";
import { ApiService, handlePublicApiError } from "src/services/api/api.service";
import { ConfigurationService } from "src/services/configuration/configuration.service";
import { LockersService } from "src/services/lockers/lockers.service";
import { ProfileService } from "src/services/profile/profile.service";
import { ToastService } from "src/services/toast/toast.service";
import { MappedRoleExtensionType } from "../../../types";

@Component({
  selector: "app-cbox-profile-platform-user-update",
  templateUrl: "cbox-profile-platform-user-update.component.html",
  styleUrls: ["./cbox-profile-platform-user-update.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    DividerModule,
    InputGroupModule,
    InputGroupAddonModule,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    AutoFocusModule,
    DropdownModule,
    MultiSelectModule,
    CheckboxModule,
    ProgressSpinnerModule,
    InputTextareaModule,
    MessagesModule
  ]
})

export class CBoxProfilePlatformUserUpdateComponent implements OnInit {

  userSaveStatus = signal<"waiting" | "fetching" | "saving">("fetching");
  userCreateResponse = signal<CBox_ActionCreateUserResponse | undefined>(undefined);
  userForm: FormGroup;
  userData = signal<CBox_GetSelfUserResponse | undefined>(undefined);
  permissions = signal<FormattedPermission[] | undefined>(undefined);
  roles = signal<CBox_PublicRolePermissionMapping | undefined>(undefined);
  extendedRoles = signal<CBox_PublicRolePermissionMapping | undefined>(undefined);
  zones = signal<Record<number, { id: string; name: string }>[]>([]);
  zonesFetched = signal(false);
  oldUniqueIdentifier = signal<string | undefined>(undefined);
  userHasEmail = signal(false);

  showIdentifierWarning = signal(false);
  identifierChangeWarning = signal<Message[]>([{
    severity: 'warn',
    detail:
        `Atentie! Modificarea identificatorului nu este recomandata. Vom folosi noul identificator pentru rapoartele generate ulterior inclusiv pentru actiunile agentului din trecut.`
  }]);

  roleExtensions = signal<MappedRoleExtensionType[]|undefined>(undefined);
  roleExtensionsMapping = signal<Record<RoleExtensionsEnum, string> | undefined>(undefined);
  oldRoleExtensions = signal<RoleExtensionsEnum[]>([]);
  showOldExtensions = signal(false);

  get lockersForm(): FormGroup {
    return this.userForm.get("lockers") as FormGroup;
  }

  constructor(
    private profileService: ProfileService,
    private api: ApiService,
    private fb: FormBuilder,
    private lockerService: LockersService,
    private config: ConfigurationService,
    private toastService: ToastService,
    private destroyRef: DestroyRef,
    private dialogRef: MatDialogRef<CBoxProfilePlatformUserUpdateComponent>,
    @Inject(MAT_DIALOG_DATA) private userIdentifier: string,
    @Inject(PLATFORM_ID) private platformId: InjectionToken<Object>) {
      this.userForm = this.fb.group({
        name: ["", Validators.required],
        email: ["", Validators.required],
        phone: ["", Validators.required],
        notes: [""],
        zoneId: [undefined, Validators.required],
        role: ["", Validators.required],
        roleExtensions: [[]],
        identifier: [{
          value: "",
          disabled: true
        }, Validators.required],
        noPhoneNumber: [false],
        noEmailAddress: [false],
      })
    }

  ngOnInit(): void {
    this.init();
  }

  loadData(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.fetchUserData();
    }
  }

  saveUser(): void {
    this.userSaveStatus.set("saving");
    this.api.patch("backend/users/" + this.userIdentifier, this.getUpdateStructure()).subscribe(() => {
      this.dialogRef.close(true);
    }, (e: HttpErrorResponse) => {
      this.userSaveStatus.set("waiting");
      handlePublicApiError(e, this.toastService);
    });
  }

  noPhoneNumberToggled(): void {
    const f = this.userForm;
    const phoneNumber = f.get("phone");
    if (f.get("noPhoneNumber")?.value) {
      phoneNumber?.disable();
      phoneNumber?.reset();
    } else {
      phoneNumber?.enable();
    }
  }

  noEmailAddressToggled(): void {
    const f = this.userForm;
    const email = f.get("email");
    if (f.get("noEmailAddress")?.value) {
      email?.disable();
      email?.reset();
    } else {
      email?.enable();
    }
  }

  async roleExtensionsChanged(event: any): Promise<void> {
    const selectedOption = event?.itemValue;
    if (!selectedOption) {
      return;
    }

    const extensions = await this.config.getRoleExtensions();
    const selectedGrant = extensions?.canGrant?.find(
      (grant) => grant.name === selectedOption.value
    );
    const selectedGroup = selectedGrant?.group;

    this.roleExtensions.update((current) => {
      const wasSelected = !!event?.originalEvent?.selected;

      const updated = current?.map((roleExtension) => {
        const roleGrant = extensions?.canGrant?.find(
          (grant) => grant.name === roleExtension.value
        );
        const roleGroup = roleGrant?.group;

        let shouldDisable = false;

        if (wasSelected) {
          // Disable other items in the same group when an item is selected
          shouldDisable =
            (selectedGroup !== undefined &&
            roleExtension.value !== selectedOption.value &&
            roleGroup === selectedGroup) ||
            roleExtension.disabled;
        } else {
          // When an item is deselected, re-enable items in the same group
          if (roleGroup === selectedGroup) {
            shouldDisable = false;
          } else {
            // Preserve existing disabled state for other groups
            shouldDisable = roleExtension.disabled;
          }
        }

        return {
          ...roleExtension,
          disabled: shouldDisable,
        };
      });

      return updated;
    });
  }

  private async init(): Promise<void> {
    const roles = await this.config.getRolePermissionMapping()
    this.roles.set(this.filterRoles(roles));
    await this.setRoleExtensions();
    await this.fetchZones();
    this.loadData();
    this.userForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      const identifierChanged = value.identifier !== this.oldUniqueIdentifier() && value.customIdentifier;
      this.showIdentifierWarning.set(identifierChanged);

      const sortedExtensions = value.roleExtensions.sort();
      this.showOldExtensions.set(JSON.stringify(sortedExtensions) !== JSON.stringify(this.oldRoleExtensions()));
    });
  }

  private fetchUserData(): void {
    this.api.get<CBox_PublicSuccessResponse<CBox_GetOtherUserResponse>>("backend/users/" + this.userIdentifier).subscribe((response) => {
      const data = response.data;
      this.userForm.patchValue({
        name: data.name,
        identifier: data.identifier,
        email: data.email,
        phone: data.phone,
        notes: data.notes,
        role: data.role,
        roleExtensions: data.roleExtensions,
        zoneId: +data.zoneId,
        noPhoneNumber: !!!data.phone,
        noEmailAddress: !!!data.email
      });
      this.noPhoneNumberToggled();
      this.noEmailAddressToggled();
      this.oldRoleExtensions.set(data.roleExtensions.sort());
      if (data.email) {
        this.userHasEmail.set(true);
      }
      this.updateRoleExtensionsDisabledState();
      this.userSaveStatus.set("waiting");
    }, (e: HttpErrorResponse) => {
      this.dialogRef.close();
      handlePublicApiError(e, this.toastService);
    });
  }

  private async fetchZones(): Promise<void> {
    return new Promise((resolve) => {
      this.api.get<CBox_PublicSuccessResponse<(CBox_GetLockerShortData & {
        zones: {
          id: string;
          name: string;
        }[]
      })[]>>("backend/locker/identifiers?includeZones=true").subscribe((response) => {
        const zones: {id: string; name: string}[] = [];
        for (const locker of response.data) {
          for (const zone of locker.zones) {
            if (!zones.find(z => z.id === zone.id)) {
              zones.push({id: zone.id, name: zone.name});
            }
          }
        }
        this.zones.set(zones);
        this.zonesFetched.set(true);
        resolve();
      }, (e: HttpErrorResponse) => {
        resolve();
        handlePublicApiError(e, this.toastService);
      });
    });
  }

  mapRoleExtensions(roleExtensions: RoleExtensionsEnum[]): string {
    return roleExtensions.map((extension) => {
      return this.config.mapExtendedPermissions(extension).toLowerCase();
    }).join(", ");
  }

  private filterRoles(roles: CBox_PublicRolePermissionMapping): CBox_PublicRolePermissionMapping {
    return roles.filter((role) => role.canCreate);
  }

  private async setRoleExtensions(): Promise<void> {
    this.roleExtensionsMapping.set(this.config.roleExtensionsMapping());
    const extensions = await this.config.getRoleExtensions();
    const roleExtensions: MappedRoleExtensionType[] = [];
    for (const extension of extensions.canGrant) {
      roleExtensions.push({
        value: extension.name,
        label: this.config.mapExtendedPermissions(extension.name),
        disabled: false
      });
    }

    this.roleExtensions.set(roleExtensions);
  }

  private async updateRoleExtensionsDisabledState(): Promise<void> {
    const selectedValues = this.userForm.get('roleExtensions')?.value || [];
    const extensions = await this.config.getRoleExtensions();

    const selectedGroups = new Set(
      extensions.canGrant
        .filter((ext) => selectedValues.includes(ext.name))
        .map((ext) => ext.group)
    );

    this.roleExtensions.update((current) => {
      return current?.map((roleExtension) => {
        const roleGrant = extensions.canGrant.find((grant) => grant.name === roleExtension.value);
        const roleGroup = roleGrant?.group;

        const isSelected = selectedValues.includes(roleExtension.value);
        const isGroupSelected = selectedGroups.has(roleGroup || "");

        return {
          ...roleExtension,
          disabled: isGroupSelected && !isSelected,
        };
      });
    });
  }

  private getUpdateStructure(): CBox_ActionUpdateOtherUserData["body"] {
    const noEmailAddress = this.userForm.get("noEmailAddress")?.value;
    const noPhoneNumber = this.userForm.get("noPhoneNumber")?.value;
    const data: CBox_ActionUpdateOtherUserData["body"] = {
      name: this.userForm.get("name")?.value,
      email: noEmailAddress ? undefined : this.userForm.get("email")?.value,
      phone: noPhoneNumber ? undefined : this.userForm.get("phone")?.value,
      role: this.userForm.get("role")?.value,
      roleExtensions: this.userForm.get("roleExtensions")?.value,
      zoneId: this.userForm.get("zoneId")?.value,
      // lockers: {
      //   all: this.userForm.get("lockers.all")?.value,
      //   specific: this.userForm.get("lockers.all")?.value ?
      //       undefined :
      //       this.formatLockers(this.userForm.get("lockers.specific")?.value)
      // },
      notes: this.userForm.get("notes")?.value,
    };

    // if (data.lockers.all) {
    //   delete data.lockers.specific;
    // } else {
    //   delete data.lockers.all;
    // }

    return data;
  }

  // private formatLockers(lockerIds: string[]): CBox_ActionCreateUserData["lockers"]["specific"] {
  //   return lockerIds.map((id) => {
  //     return {
  //       id
  //     }
  //   });
  // }
}